/**
 * /fiches/moto/premiere-circulation-a2 — Fiche pédagogique imprimable A4
 * pour la 1ère leçon de circulation moto A2 (post-plateau).
 *
 * Basée sur le référentiel REMC — Compétences C2 (Circuler dans des conditions normales).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Printer, Download, ArrowLeft, QrCode, X, Copy, Check } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

export default function FichePremiereCirculationA2() {
  const [showQR, setShowQR] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    document.title = 'Fiche 1ère leçon circulation moto A2 · code2roues';
  }, []);

  const printPage = () => window.print();
  const publicUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/fiches/eleve/premiere-circulation-a2`
    : 'https://code2roues.fr/fiches/eleve/premiere-circulation-a2';

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) { /* silent */ }
  };

  return (
    <div className="min-h-screen bg-neutral-100 print:bg-white">
      {/* Barre supérieure (non imprimable) */}
      <div className="print:hidden bg-neutral-900 border-b border-white/10 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/moniteur/planning" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-[13px] font-semibold">
            <ArrowLeft size={14} /> Retour au BO moniteur
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowQR(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/15 bg-white/[0.04] hover:bg-white/[0.10] text-white/85 text-[12px] font-semibold transition"
              data-testid="btn-share-qr"
            >
              <QrCode size={13} /> Partager (QR)
            </button>
            <a
              href="/fiches/moto/premiere-circulation-a2.pdf"
              download
              onClick={(e) => { e.preventDefault(); printPage(); }}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/15 bg-white/[0.04] hover:bg-white/[0.10] text-white/85 text-[12px] font-semibold transition"
              data-testid="btn-download-pdf"
            >
              <Download size={13} /> Télécharger PDF
            </a>
            <button
              onClick={printPage}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-[12px] font-semibold shadow-md transition"
              data-testid="btn-print-fiche"
            >
              <Printer size={13} /> Imprimer
            </button>
          </div>
        </div>
      </div>

      {/* Feuille A4 */}
      <div className="max-w-4xl mx-auto py-6 print:py-0 print:max-w-none">
        <div
          className="bg-white shadow-lg print:shadow-none mx-auto text-neutral-900"
          style={{ width: '210mm', minHeight: '297mm', padding: '14mm 15mm', fontFamily: 'Figtree, system-ui, sans-serif' }}
          data-testid="fiche-sheet"
        >
          {/* En-tête */}
          <header className="flex items-start justify-between border-b-2 border-sky-600 pb-3 mb-4 gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-[9px] font-black tracking-[0.28em] uppercase text-sky-600">code2roues.fr · Fiche pédagogique</div>
              <h1 className="text-[22px] font-black text-neutral-900 leading-tight mt-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                1<sup>ère</sup> leçon moto — Circulation A2
              </h1>
              <div className="text-[11px] text-neutral-600 mt-0.5">Formation initiale · ~2 h · Référentiel REMC compétences C2</div>
            </div>
            {/* Mini QR imprimé */}
            <div className="flex flex-col items-center shrink-0">
              <div className="bg-white p-1 border border-neutral-300 rounded">
                <QRCodeSVG value={publicUrl} size={64} level="M" includeMargin={false} bgColor="#ffffff" fgColor="#0a0a0b" />
              </div>
              <div className="text-[7.5px] font-bold uppercase tracking-widest text-neutral-500 text-center mt-1 leading-tight">
                Scan pour<br />garder ce cours
              </div>
            </div>
          </header>

          {/* Bloc identification */}
          <section className="grid grid-cols-3 gap-3 mb-4 text-[11px]">
            <IdField label="Élève (nom prénom)" />
            <IdField label="Date de la leçon" />
            <IdField label="Moniteur / auto-école" />
          </section>

          {/* Rappel du cadre officiel */}
          <section className="mb-3 border-l-4 border-sky-600 bg-sky-50 pl-3 py-2 pr-2 rounded-r">
            <div className="text-[10px] font-black uppercase tracking-widest text-sky-700 mb-0.5">📌 Cadre officiel — REMC C2</div>
            <div className="text-[10.5px] text-neutral-800 leading-snug">
              Cette 1<sup>ʳᵉ</sup> leçon de circulation intervient après validation du <strong>plateau</strong>. Objectif : passer du terrain fermé à la route en toute sécurité. Elle couvre les <strong>fondamentaux de la compétence C2</strong> : positionnement, trajectoires, contrôles visuels, communication et franchissement d&apos;intersections simples.
            </div>
            <div className="text-[9px] text-sky-700 italic mt-0.5">Référentiel REMC (Référentiel pour l&apos;Éducation à une Mobilité Citoyenne) · Sécurité Routière</div>
          </section>

          {/* Objectifs */}
          <section className="mb-3">
            <SectionTitle>🎯 Objectifs de la 1ʳᵉ leçon</SectionTitle>
            <ul className="text-[10.5px] text-neutral-800 space-y-0.5 pl-4 list-disc mt-1">
              <li>Prendre confiance dans la circulation à faible densité</li>
              <li>Maîtriser les 3 contrôles visuels systématiques : rétros + épaule (angle mort)</li>
              <li>Adopter un positionnement sécurisé et une trajectoire correcte</li>
              <li>Franchir les 3 types d&apos;intersections simples (stop, cédez-le-passage, priorité à droite)</li>
              <li>Communiquer clairement avec les autres usagers (clignotants, position, regard)</li>
            </ul>
          </section>

          {/* Module Préparation */}
          <Module title="Préparation : VVR & rappels sécurité" duration="10 min" competence="C2 · Vérifications préalables">
            <CheckLine text="VVR (Vérification Visuelle Réglementaire) : freins, pneus, feux, direction, niveaux, chaîne" />
            <CheckLine text="EPI complets : casque bouclé, gants CE, blouson protégé, chaussures montantes, pantalon adapté" />
            <CheckLine text="Réglage des rétroviseurs (voir juste le bout du coude)" />
            <CheckLine text="Briefing itinéraire + zones piégeuses du parcours du jour" />
            <CheckLine text="Consignes radio-oreillette (si utilisées) et rappel des gestes conventionnels" />
          </Module>

          {/* Étape 1 */}
          <Module title="Étape 1 — Départ en circulation" duration="15 min" competence="C2-01 · Démarrer en sécurité">
            <CheckLine text="1. Regard rétro gauche + contrôle angle mort (épaule gauche)" />
            <CheckLine text="2. Clignotant gauche pour signaler l'intention de partir" />
            <CheckLine text="3. Démarrage progressif + montée en 2ᵉ dès que possible" />
            <CheckLine text="4. Retour en position de circulation à droite (tiers droit de la voie)" />
            <CheckLine text="5. Extinction du clignotant après manœuvre" />
          </Module>

          {/* Étape 2 */}
          <Module title="Étape 2 — Circulation en zone calme" duration="30 min" competence="C2-02 · Positionnement & trajectoire">
            <CheckLine text="1. Regard haut, loin, mobile (jamais fixé sur la roue avant)" />
            <CheckLine text="2. Position sur voie : tiers droit en ligne droite, éviter les rails/plaques/gravillons" />
            <CheckLine text="3. Trajectoire de sécurité : Extérieur → Intérieur → Extérieur dans les virages" />
            <CheckLine text="4. Adaptation de la vitesse au type de voie (30 · 50 · 70 · 90)" />
            <CheckLine text="5. Distance de sécurité : au moins 2 secondes derrière un véhicule" />
          </Module>

          {/* Page break */}
          <div className="print:break-before-page hidden print:block" />

          {/* Étape 3 */}
          <Module title="Étape 3 — Contrôles visuels & Communication" duration="15 min" competence="C2-03 · Communiquer">
            <CheckLine text="1. Séquence complète : Rétro → Épaule (angle mort) → Clignotant → Action" />
            <CheckLine text="2. Se rendre visible : placement dans la voie, phares allumés (obligatoire moto)" />
            <CheckLine text="3. Anticiper : lire la route 10-15 secondes en avance" />
            <CheckLine text="4. Utilisation appropriée du klaxon (avertir, jamais agresser)" />
          </Module>

          {/* Étape 4 */}
          <Module title="Étape 4 — Franchissement d'intersections simples" duration="30 min" competence="C2-04 · Intersections">
            <CheckLine text="1. Intersection avec STOP : arrêt complet ligne d'effet, pied gauche à terre, contrôle 360°" />
            <CheckLine text="2. Cédez-le-passage : ralentir, contrôler, s'insérer sans gêner" />
            <CheckLine text="3. Priorité à droite : anticipation visuelle avant chaque intersection non signalisée" />
            <CheckLine text="4. Feu tricolore : distance d'arrêt suffisante, ligne d'effet respectée" />
            <CheckLine text="5. Rond-point : cédez à ceux qui sont sur l'anneau, clignotant droit pour sortir" />
          </Module>

          {/* Étape 5 */}
          <Module title="Étape 5 — Retour au point de départ" duration="10 min" competence="C2 · Autonomie progressive">
            <CheckLine text="1. Itinéraire de retour en autonomie guidée (le moniteur donne les grandes lignes)" />
            <CheckLine text="2. Application des acquis de la séance sans consigne à chaque manœuvre" />
            <CheckLine text="3. Arrêt final : positionnement en épi ou stationnement adapté" />
            <CheckLine text="4. Coupure moteur, béquille, retrait clé + verrouillage direction" />
          </Module>

          {/* Bilan */}
          <Module title="Bilan de fin de leçon" duration="10 min" competence="Débrief moniteur / élève">
            <div className="grid grid-cols-2 gap-3 mt-2">
              <BilanBox label="✅ Points forts observés" />
              <BilanBox label="⚠️ Points à travailler" />
            </div>
            <div className="mt-2">
              <BilanBox label="📌 Programme prochaine leçon" small />
            </div>
          </Module>

          {/* Signatures */}
          <section className="grid grid-cols-2 gap-6 mt-6">
            <SignatureBox label="Signature élève" />
            <SignatureBox label="Signature moniteur" />
          </section>

          {/* Page 2 — Annexe visuelle */}
          <div className="print:break-before-page" />
          <section className="mt-8 print:mt-0" data-testid="annexe-circulation">
            <div className="border-b-2 border-sky-600 pb-2 mb-3">
              <div className="text-[9px] font-black tracking-[0.28em] uppercase text-sky-600">Annexe · Fondamentaux route</div>
              <h2 className="text-[18px] font-black text-neutral-900 leading-tight mt-0.5" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Les 6 clés de la 1ʳᵉ circulation moto
              </h2>
              <div className="text-[10.5px] text-neutral-600 mt-0.5">Repères visuels essentiels à intégrer dès la 1<sup>ʳᵉ</sup> leçon de route.</div>
            </div>

            <div className="grid grid-cols-2 gap-2.5">
              <FicheEtape
                n="1"
                color="#0284c7"
                title="Le regard : haut, loin, mobile"
                specs={['Jamais fixer la roue avant', 'Balayer la scène routière', 'Anticiper 10-15 sec en avance']}
                svg={<SVGRegard />}
              />
              <FicheEtape
                n="2"
                color="#0891b2"
                title="Positionnement sur la voie"
                specs={['Tiers droit en ligne droite', 'Éviter rails, gravillons, plaques', 'Se rendre visible aux autres']}
                svg={<SVGPositionnement />}
              />
              <FicheEtape
                n="3"
                color="#059669"
                title="Trajectoire de sécurité"
                specs={['Extérieur → Intérieur → Extérieur', 'Freiner AVANT le virage', 'Accélérer en sortie de courbe']}
                svg={<SVGTrajectoire />}
              />
              <FicheEtape
                n="4"
                color="#d97706"
                title="Les 3 contrôles visuels"
                specs={['Rétro → Épaule (angle mort) → Clignotant → Action', 'Automatisme à ancrer dès J1']}
                svg={<SVGControles />}
              />
              <FicheEtape
                n="5"
                color="#dc2626"
                title="Distance de sécurité"
                specs={['2 secondes minimum en sec', '4 secondes en pluie', 'Compter « 1 tulipe, 2 tulipes »']}
                svg={<SVGDistance />}
              />
              <FicheEtape
                n="6"
                color="#7c3aed"
                title="Intersections simples"
                specs={['STOP : arrêt complet, pied à terre', 'Cédez : ralentir + contrôler', 'PAD : anticipation systématique']}
                svg={<SVGIntersection />}
              />
            </div>

            {/* Encadrés récap */}
            <div className="mt-3 grid grid-cols-2 gap-2 text-[10px]">
              <div className="border border-emerald-400 bg-emerald-50 rounded p-2">
                <div className="font-bold text-emerald-800 mb-0.5">✅ Les bons réflexes moto</div>
                <ul className="list-disc pl-3.5 space-y-0.5 text-emerald-900">
                  <li>Phares allumés en permanence (obligatoire moto)</li>
                  <li>Regard haut → la moto va où on regarde</li>
                  <li>Freiner à 70% avant + 30% arrière (progressivement)</li>
                  <li>Contre-braquage : appuyer sur le guidon <strong>côté virage</strong></li>
                  <li>Se rendre visible : position + gestes + clignotant</li>
                </ul>
              </div>
              <div className="border border-red-400 bg-red-50 rounded p-2">
                <div className="font-bold text-red-800 mb-0.5">⛔ Erreurs classiques à éviter</div>
                <ul className="list-disc pl-3.5 space-y-0.5 text-red-900">
                  <li>Rouler dans la <strong>trace d&apos;huile</strong> (centre de voie)</li>
                  <li>Oublier l&apos;<strong>angle mort</strong> avant de tourner</li>
                  <li>Freiner <strong>dans</strong> le virage</li>
                  <li>Suivre <strong>trop près</strong> une voiture</li>
                  <li>Sur-régime en 1ʳᵉ ou 2ᵉ inutilement</li>
                </ul>
              </div>
            </div>

            <div className="mt-3 text-[9px] text-neutral-500 italic">
              Fiche pédagogique conforme au référentiel REMC · Compétences C2 (Circuler dans des conditions normales) · Formation initiale permis A2.
            </div>
          </section>

          {/* Pied */}
          <footer className="mt-4 pt-3 border-t border-neutral-200 flex items-center justify-between text-[9px] text-neutral-500">
            <div>code2roues.fr · Fiche 1ère leçon circulation moto A2 · v1.0</div>
            <div>1/1</div>
          </footer>
        </div>
      </div>

      {/* Modale QR code */}
      {showQR && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 print:hidden"
          onClick={() => setShowQR(false)}
          data-testid="modal-qr-share"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-neutral-900 text-white px-5 py-3 flex items-center justify-between">
              <div>
                <div className="text-[9px] font-black uppercase tracking-[0.22em] text-sky-400">Partage rapide</div>
                <div className="text-[14px] font-bold">1ʳᵉ circulation A2</div>
              </div>
              <button
                onClick={() => setShowQR(false)}
                className="text-white/60 hover:text-white p-1"
                data-testid="btn-close-qr"
                aria-label="Fermer"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5 flex flex-col items-center">
              <div className="bg-white p-3 rounded-lg border-2 border-neutral-200">
                <QRCodeSVG value={publicUrl} size={220} level="H" includeMargin={false} bgColor="#ffffff" fgColor="#0a0a0b" />
              </div>
              <div className="mt-4 text-center">
                <div className="text-[11px] text-neutral-500 uppercase tracking-widest font-bold mb-1">Scannez avec un smartphone</div>
                <div className="text-[13px] text-neutral-800">Votre élève accède à la fiche complète en 1 clic</div>
              </div>
              <div className="mt-4 w-full">
                <div className="flex items-center gap-2 border border-neutral-300 rounded-lg px-3 py-2 bg-neutral-50">
                  <div className="flex-1 text-[11px] text-neutral-700 truncate font-mono" data-testid="qr-url">{publicUrl}</div>
                  <button
                    onClick={copyUrl}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-bold transition ${
                      copied ? 'bg-emerald-600 text-white' : 'bg-neutral-900 text-white hover:bg-neutral-700'
                    }`}
                    data-testid="btn-copy-url"
                  >
                    {copied ? <><Check size={11} /> Copié</> : <><Copy size={11} /> Copier</>}
                  </button>
                </div>
              </div>
              <div className="mt-3 text-[10.5px] text-neutral-500 text-center leading-snug">
                Vous pouvez aussi imprimer ce QR code sur le support papier ou l&apos;envoyer par SMS/WhatsApp.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Styles impression */}
      <style>{`
        @page { size: A4; margin: 0; }
        @media print {
          html, body { background: #fff !important; }
          .print\\:hidden { display: none !important; }
        }
      `}</style>
    </div>
  );
}

// ─── Sous-composants ─────────────────────────────────────────────────
function IdField({ label }) {
  return (
    <div className="border border-neutral-300 rounded p-1.5 h-[42px] flex flex-col justify-between">
      <div className="text-[8.5px] font-bold uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="border-b border-dotted border-neutral-400 h-3" />
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="text-[10.5px] font-black uppercase tracking-widest text-sky-600 border-b border-sky-200 pb-0.5">
      {children}
    </div>
  );
}

function Module({ title, duration, competence, children }) {
  return (
    <section className="mb-3 avoid-page-break">
      <div className="flex items-baseline justify-between border-b border-neutral-300 pb-1 mb-1.5">
        <h2 className="text-[13px] font-black text-neutral-900" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          {title}
        </h2>
        <div className="text-[9.5px] text-neutral-600">
          <span className="inline-block bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded font-bold mr-1.5">{duration}</span>
          <span className="italic">{competence}</span>
        </div>
      </div>
      <div className="space-y-0.5">{children}</div>
    </section>
  );
}

function CheckLine({ text }) {
  return (
    <div className="flex items-start gap-2 text-[10.5px] text-neutral-800 leading-snug">
      <span className="inline-block w-[11px] h-[11px] border border-neutral-500 rounded-[2px] shrink-0 mt-[2px]" />
      <span>{text}</span>
    </div>
  );
}

function BilanBox({ label, small }) {
  return (
    <div>
      <div className="text-[9.5px] font-bold uppercase tracking-widest text-neutral-600 mb-1">{label}</div>
      <div className="border border-neutral-300 rounded" style={{ height: small ? '18mm' : '22mm' }} />
    </div>
  );
}

function SignatureBox({ label }) {
  return (
    <div>
      <div className="text-[9.5px] font-bold uppercase tracking-widest text-neutral-600 mb-1">{label}</div>
      <div className="border border-neutral-300 rounded" style={{ height: '20mm' }} />
    </div>
  );
}

function FicheEtape({ n, color, title, specs, svg }) {
  return (
    <div className="border-2 rounded-lg p-2 bg-white flex flex-col" style={{ borderColor: color }}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <span
          className="inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-[11px] font-black shrink-0"
          style={{ background: color }}
        >
          {n}
        </span>
        <div className="text-[11px] font-black text-neutral-900 leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          {title}
        </div>
      </div>
      <div className="bg-neutral-50 border border-neutral-200 rounded mb-1.5 p-1">
        {svg}
      </div>
      <ul className="text-[8.5px] text-neutral-700 space-y-0.5 list-disc pl-3.5 leading-tight">
        {specs.map((s, i) => <li key={i}>{s}</li>)}
      </ul>
    </div>
  );
}

/* ─── 6 mini-schémas SVG ─────────────────────────────────────────── */

/** 1. Regard : yeux + cône de vision qui balaye */
function SVGRegard() {
  return (
    <svg viewBox="0 0 240 100" className="w-full h-auto" role="img" aria-label="Regard haut">
      <rect x="10" y="10" width="220" height="80" fill="#ffffff" stroke="#cbd5e1" strokeWidth="1" strokeDasharray="3 2" rx="4" />
      {/* Moto (vue arrière) */}
      <circle cx="40" cy="70" r="6" fill="#0284c7" />
      <rect x="36" y="55" width="8" height="15" fill="#0284c7" />
      <text x="40" y="88" textAnchor="middle" fontSize="6.5" fontWeight="700" fill="#0284c7">MOTO</text>
      {/* Cône de vision loin */}
      <path d="M 46 60 L 220 25 L 220 55 Z" fill="#0284c7" fillOpacity="0.12" stroke="#0284c7" strokeWidth="1" strokeDasharray="3 2" />
      {/* Horizon */}
      <line x1="60" y1="30" x2="225" y2="30" stroke="#94a3b8" strokeWidth="1" strokeDasharray="1 2" />
      <text x="140" y="22" textAnchor="middle" fontSize="7" fontWeight="700" fill="#0284c7">HORIZON — regard 10-15 s en avance</text>
      <text x="140" y="48" textAnchor="middle" fontSize="6.5" fill="#075985" fontStyle="italic">La moto va là où le regard va</text>
    </svg>
  );
}

/** 2. Positionnement : voie découpée en 3 tiers */
function SVGPositionnement() {
  return (
    <svg viewBox="0 0 240 100" className="w-full h-auto" role="img" aria-label="Positionnement voie">
      <rect x="10" y="10" width="220" height="80" fill="#4b5563" rx="4" />
      {/* Ligne centrale */}
      <line x1="120" y1="15" x2="120" y2="85" stroke="#facc15" strokeWidth="2" strokeDasharray="8 4" />
      {/* Voie de droite : 3 tiers */}
      <line x1="157" y1="15" x2="157" y2="85" stroke="#94a3b8" strokeWidth="0.8" strokeDasharray="2 2" />
      <line x1="195" y1="15" x2="195" y2="85" stroke="#94a3b8" strokeWidth="0.8" strokeDasharray="2 2" />
      {/* Trace huile (à éviter) */}
      <rect x="130" y="20" width="18" height="60" fill="#0f172a" fillOpacity="0.7" />
      <text x="139" y="53" textAnchor="middle" fontSize="6" fontWeight="800" fill="#f87171">huile</text>
      {/* Moto positionnée dans tiers droit */}
      <circle cx="210" cy="50" r="8" fill="#0891b2" stroke="#ffffff" strokeWidth="2" />
      <text x="210" y="53" textAnchor="middle" fontSize="8" fontWeight="900" fill="#ffffff">M</text>
      {/* Labels */}
      <text x="140" y="98" textAnchor="middle" fontSize="6.5" fontWeight="700" fill="#dc2626">1/3 gauche</text>
      <text x="175" y="98" textAnchor="middle" fontSize="6.5" fontWeight="700" fill="#f59e0b">1/3 centre</text>
      <text x="210" y="98" textAnchor="middle" fontSize="6.5" fontWeight="900" fill="#22d3ee">1/3 droit ✓</text>
    </svg>
  );
}

/** 3. Trajectoire de sécurité : Ext → Int → Ext dans un virage à droite */
function SVGTrajectoire() {
  return (
    <svg viewBox="0 0 240 100" className="w-full h-auto" role="img" aria-label="Trajectoire de sécurité">
      <rect x="10" y="10" width="220" height="80" fill="#ffffff" stroke="#cbd5e1" strokeWidth="1" rx="4" />
      {/* Bord ext de la route */}
      <path d="M 15 80 Q 60 80 90 60 Q 130 30 220 30" stroke="#4b5563" strokeWidth="3" fill="none" />
      {/* Bord int */}
      <path d="M 15 55 Q 60 55 90 40 Q 130 10 220 10" stroke="#4b5563" strokeWidth="3" fill="none" />
      {/* Trajectoire idéale (moto) */}
      <path d="M 20 75 Q 60 75 95 55 Q 130 32 220 22" stroke="#059669" strokeWidth="2.5" fill="none" strokeDasharray="5 3" markerEnd="url(#tr-ar)" />
      {/* Points EXT / INT / EXT */}
      <circle cx="25" cy="75" r="4" fill="#059669" />
      <text x="25" y="88" textAnchor="middle" fontSize="6.5" fontWeight="800" fill="#059669">EXT</text>
      <circle cx="100" cy="54" r="4" fill="#059669" />
      <text x="100" y="48" textAnchor="middle" fontSize="6.5" fontWeight="800" fill="#059669">INT (corde)</text>
      <circle cx="215" cy="22" r="4" fill="#059669" />
      <text x="215" y="14" textAnchor="middle" fontSize="6.5" fontWeight="800" fill="#059669">EXT</text>
      <defs>
        <marker id="tr-ar" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="#059669" />
        </marker>
      </defs>
    </svg>
  );
}

/** 4. Contrôles : séquence Rétro → Épaule → Clignotant → Action */
function SVGControles() {
  return (
    <svg viewBox="0 0 240 100" className="w-full h-auto" role="img" aria-label="Contrôles visuels">
      <rect x="10" y="10" width="220" height="80" fill="#ffffff" stroke="#cbd5e1" strokeWidth="1" strokeDasharray="3 2" rx="4" />
      {/* Étape 1 : Rétro */}
      <circle cx="35" cy="50" r="14" fill="#d97706" />
      <text x="35" y="54" textAnchor="middle" fontSize="10" fontWeight="900" fill="#ffffff">👁</text>
      <text x="35" y="78" textAnchor="middle" fontSize="6.5" fontWeight="800" fill="#92400e">Rétro</text>
      <path d="M 52 50 L 68 50" stroke="#d97706" strokeWidth="1.5" markerEnd="url(#ct-ar)" />
      {/* Étape 2 : Épaule */}
      <circle cx="85" cy="50" r="14" fill="#d97706" />
      <text x="85" y="54" textAnchor="middle" fontSize="12" fontWeight="900" fill="#ffffff">↩</text>
      <text x="85" y="78" textAnchor="middle" fontSize="6.5" fontWeight="800" fill="#92400e">Épaule</text>
      <path d="M 102 50 L 118 50" stroke="#d97706" strokeWidth="1.5" markerEnd="url(#ct-ar)" />
      {/* Étape 3 : Clignotant */}
      <circle cx="135" cy="50" r="14" fill="#d97706" />
      <text x="135" y="55" textAnchor="middle" fontSize="12" fontWeight="900" fill="#ffffff">💡</text>
      <text x="135" y="78" textAnchor="middle" fontSize="6.5" fontWeight="800" fill="#92400e">Clignotant</text>
      <path d="M 152 50 L 168 50" stroke="#d97706" strokeWidth="1.5" markerEnd="url(#ct-ar)" />
      {/* Étape 4 : Action */}
      <circle cx="195" cy="50" r="14" fill="#059669" />
      <text x="195" y="54" textAnchor="middle" fontSize="10" fontWeight="900" fill="#ffffff">✓</text>
      <text x="195" y="78" textAnchor="middle" fontSize="6.5" fontWeight="800" fill="#065f46">Action</text>
      <text x="120" y="22" textAnchor="middle" fontSize="7" fontWeight="700" fill="#92400e">Séquence obligatoire — dans l&apos;ordre</text>
      <defs>
        <marker id="ct-ar" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="#d97706" />
        </marker>
      </defs>
    </svg>
  );
}

/** 5. Distance de sécurité : 2 véhicules avec chrono */
function SVGDistance() {
  return (
    <svg viewBox="0 0 240 100" className="w-full h-auto" role="img" aria-label="Distance de sécurité">
      <rect x="10" y="10" width="220" height="80" fill="#4b5563" rx="4" />
      <line x1="10" y1="50" x2="230" y2="50" stroke="#facc15" strokeWidth="1.5" strokeDasharray="6 4" />
      {/* Voiture devant */}
      <rect x="170" y="38" width="32" height="24" fill="#e5e7eb" stroke="#0f172a" strokeWidth="1" rx="3" />
      <text x="186" y="53" textAnchor="middle" fontSize="7" fontWeight="800" fill="#0f172a">🚗</text>
      {/* Moto derrière */}
      <circle cx="55" cy="50" r="10" fill="#dc2626" stroke="#ffffff" strokeWidth="2" />
      <text x="55" y="54" textAnchor="middle" fontSize="9" fontWeight="900" fill="#ffffff">🏍</text>
      {/* Ligne distance */}
      <line x1="66" y1="70" x2="168" y2="70" stroke="#22d3ee" strokeWidth="1.5" strokeDasharray="3 2" markerStart="url(#d-ar-l)" markerEnd="url(#d-ar-r)" />
      <rect x="85" y="75" width="60" height="12" fill="#dc2626" rx="2" />
      <text x="115" y="84" textAnchor="middle" fontSize="8" fontWeight="900" fill="#ffffff">2 sec min</text>
      <text x="115" y="25" textAnchor="middle" fontSize="7" fontWeight="700" fill="#facc15">« 1 tulipe · 2 tulipes »</text>
      <defs>
        <marker id="d-ar-l" viewBox="0 0 10 10" refX="2" refY="5" markerWidth="5" markerHeight="5" orient="auto">
          <path d="M 10 0 L 0 5 L 10 10 z" fill="#22d3ee" />
        </marker>
        <marker id="d-ar-r" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="#22d3ee" />
        </marker>
      </defs>
    </svg>
  );
}

/** 6. Intersection : croix avec panneau STOP */
function SVGIntersection() {
  return (
    <svg viewBox="0 0 240 100" className="w-full h-auto" role="img" aria-label="Intersection">
      <rect x="10" y="10" width="220" height="80" fill="#ffffff" stroke="#cbd5e1" strokeWidth="1" rx="4" />
      {/* Routes en croix */}
      <rect x="90" y="15" width="60" height="70" fill="#4b5563" />
      <rect x="15" y="40" width="210" height="20" fill="#4b5563" />
      {/* Ligne d'effet stop */}
      <line x1="90" y1="62" x2="150" y2="62" stroke="#ffffff" strokeWidth="2" />
      {/* Panneau STOP */}
      <polygon points="120,72 130,72 133,78 130,84 120,84 117,78" fill="#dc2626" stroke="#ffffff" strokeWidth="1" />
      <text x="125" y="80" textAnchor="middle" fontSize="5" fontWeight="900" fill="#ffffff">STOP</text>
      {/* Moto qui arrive */}
      <circle cx="120" cy="78" r="5" fill="#7c3aed" opacity="0" />
      <text x="125" y="92" textAnchor="middle" fontSize="6" fontWeight="800" fill="#7c3aed">Arrêt complet + pied à terre</text>
      {/* Flèches contrôle 360 */}
      <path d="M 60 50 L 88 50" stroke="#7c3aed" strokeWidth="1.5" markerEnd="url(#i-ar)" />
      <path d="M 180 50 L 152 50" stroke="#7c3aed" strokeWidth="1.5" markerEnd="url(#i-ar)" />
      <text x="45" y="46" textAnchor="middle" fontSize="6.5" fontWeight="700" fill="#7c3aed">← contrôle</text>
      <text x="195" y="46" textAnchor="middle" fontSize="6.5" fontWeight="700" fill="#7c3aed">contrôle →</text>
      <defs>
        <marker id="i-ar" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="#7c3aed" />
        </marker>
      </defs>
    </svg>
  );
}
